import { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import { dashboardApi } from '@/core/api';
import type { ResponseDashboardByTowProviderDTO } from '@/core/api';
import { fromAxiosError } from '@/core/api';
import { daysAgoIso } from '@/core/utils';
import { BackToTopFab, HelpAlert, usePageTitle } from '@/shared';
import KpiCard from './KpiCard';
import KpiCardSkeleton from './KpiCardSkeleton';
import { kpiVisual, sectionEyebrowSx } from './kpiVisuals';
import KpiIcon from './KpiIcon';
import ProviderActiveJobs from './ProviderActiveJobs';
import OfferConversionChart from './OfferConversionChart';

/**
 * Drill-down for a KPI tile: the tow-request list, scoped to the tile's window.
 *
 * Every tile links now. Previously only two of the eight did — the whole
 * "Last Week" row and half of "Month" were dead, so a dispatcher clicking a
 * number they wanted to investigate got nothing back.
 *
 * The window is the only filter applied, and that is deliberate. `winnerRequest`
 * and `ammount` both count offers that *have a winnerDate*, and a won request
 * goes on to live under WINNER, TOARRIVE, INPROCESS or COMPLETED — the list's
 * `status` filter takes a single value, so filtering by any one of them would
 * show fewer rows than the tile claims. A window that over-shows is honest; a
 * status filter that under-shows quietly contradicts the number above it.
 *
 * The old month `offerRequest` link passed `type: 'Offer Request'`, which the
 * list does not read — it parses claimNumber, status, startDate, endDate,
 * customerId, towProviderId and serviceType, and nothing else. It was a no-op
 * dressed up as a filter.
 */
function windowLink(days: number): string {
  return (
    '/tow-requests?' +
    new URLSearchParams({ startDate: daysAgoIso(days), isDashboard: 'true' }).toString()
  );
}

export default function TowProviderHome() {
  const { t, i18n } = useTranslation();
  usePageTitle('home.towProvider.pageTitle');

  const [data, setData] = useState<ResponseDashboardByTowProviderDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setErrorMessage(null);

    async function fetchDashboard() {
      try {
        const result = await dashboardApi.getTowProviderDashboard();
        if (!cancelled) {
          setData(result);
        }
      } catch (err) {
        if (!cancelled) {
          setErrorMessage(fromAxiosError(err).message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void fetchDashboard();

    return () => {
      cancelled = true;
    };
  }, [version]);

  if (errorMessage !== null) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <strong>{t('home.errorTitle')}: </strong>
          {errorMessage}
        </Alert>
      </Box>
    );
  }

  const tp = 'HOME.DASHBOARD.TOW-PROVIDER';

  // Each bucket is a week, labelled by its date range — "Jul 9 – 15" rather
  // than a bare start date, so nobody has to work out whether the label means
  // the week starting or ending there. The end day is derived because buckets
  // are 7 days wide by definition (see OfferActivityBucketDTO).
  //
  // `offerActivity` is tolerated as absent so a client running against an older
  // API renders the rest of the dashboard rather than blowing up on a field
  // that is not there yet.
  const bucketStartFormatter = new Intl.DateTimeFormat(i18n.language, {
    month: 'short',
    day: 'numeric',
  });
  const bucketEndFormatter = new Intl.DateTimeFormat(i18n.language, { day: 'numeric' });
  const conversionSeries = (data?.offerActivity ?? []).map((entry) => {
    const start = new Date(entry.date);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return {
      label: `${bucketStartFormatter.format(start)}–${bucketEndFormatter.format(end)}`,
      submitted: entry.submitted,
      won: entry.won,
    };
  });

  return (
    <Box sx={{ p: 3 }}>
      {/* Page header — sticky so title and refresh controls stay accessible on scroll */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          bgcolor: 'background.default',
          zIndex: 1,
          py: 1,
          mb: 2,
        }}
      >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h5" component="h1" sx={{ color: 'primary.main' }}>
          {t('home.towProvider.pageTitle')}
        </Typography>
        <Tooltip title={t('actions.refresh')}>
          <span>
            <IconButton
              aria-label={t('actions.refresh')}
              size="small"
              onClick={() => setVersion((v) => v + 1)}
              disabled={loading}
              sx={{ '@media print': { display: 'none' } }}
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      </Box>

      <HelpAlert
        persistKey="home.towprovider.welcome"
        severity="info"
        variant="outlined"
        title={t('HELP.home.towprovider.welcome.title')}
      >
        {t('HELP.home.towprovider.welcome.body')}
      </HelpAlert>

      {/* ------------------------------------------------------------------ */}
      {/* Section 1 — Towing Request (6 KPI cards)                           */}
      {/* ------------------------------------------------------------------ */}
      <Typography
        id="tow-provider-home-towing-title"
        component="h2"
        sx={sectionEyebrowSx}
      >
        {t(`${tp}.towingRequest.title`)}
      </Typography>
      <Grid
        container
        spacing={2}
        sx={{ mb: 4 }}
        aria-busy={loading || !data}
        role="region"
        aria-labelledby="tow-provider-home-towing-title"
      >
        {loading || !data
          ? Array.from({ length: 6 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} lg={2} key={i}>
                <KpiCardSkeleton />
              </Grid>
            ))
          : (
            // None of these six carries a date window, and `isDashboard=true`
            // without a `startDate` makes the API **500**: the flag's branch in
            // `TowRequestBLL.GetListTowRequest` dereferences
            // `valuesDTO.startDate.Value` unconditionally, so EF fails with
            // "An exception was thrown while attempting to evaluate a LINQ query
            // parameter expression" — which the page then shows to the driver or
            // dispatcher verbatim. Every tile in this row was hitting it.
            //
            // The flag also bought nothing here: `TowRequestsList` never sends
            // `userId`, so the request takes the admin branch, where
            // `isDashboard` force-filters `towProviderId == null` (the opposite
            // of what a provider wants) and ignores `status` and `type`
            // entirely. Dropping it makes the plain `status` filter apply, which
            // is what these tiles actually mean. See TP-2 in QA_LOG.md for the
            // backend null-guard that should land alongside this.
            [
              { key: 'open',             value: data.main.open,             linkTo: '/tow-requests?status=OPEN' },
              { key: 'openWithoutOffer', value: data.main.openWithoutOffer, linkTo: '/tow-requests?status=OPEN' },
              { key: 'winner',           value: data.main.winner,           linkTo: '/tow-requests?status=WINNER' },
              { key: 'toArrive',         value: data.main.toArrive,         linkTo: '/tow-requests?status=TOARRIVE' },
              { key: 'inProcess',        value: data.main.inProcess,        linkTo: '/tow-requests?status=INPROCESS' },
              // "delivered" maps to backend status COMPLETED — confirmed in Angular home.component.ts:699
              { key: 'delivered',        value: data.main.delivered,        linkTo: '/tow-requests?status=COMPLETED' },
            ] as const
          ).map(({ key, value, linkTo }) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={key}>
              <KpiCard
                label={t(`${tp}.towingRequest.${key}`)}
                value={value}
                icon={<KpiIcon kpiKey={key} />}
                tone={kpiVisual(key).tone}
                linkTo={linkTo}
              />
            </Grid>
          ))
        }
      </Grid>

      {/* ------------------------------------------------------------------ */}
      {/* Section 1b — Roadside Assistance (5 KPI cards)                     */}
      {/*                                                                    */}
      {/* Roadside jobs live in SrServiceRequest, an entirely separate table  */}
      {/* from CmTowRequest, so none of the six tiles above counted a single  */}
      {/* one — a provider doing roadside work saw no trace of it here.      */}
      {/* No "without offer" tile: that count has no roadside equivalent on   */}
      {/* the API, and inventing one from a different predicate would be a    */}
      {/* number that quietly means something else.                          */}
      {/* ------------------------------------------------------------------ */}
      <Typography
        id="tow-provider-home-roadside-title"
        component="h2"
        sx={sectionEyebrowSx}
      >
        {t(`${tp}.roadside.title`)}
      </Typography>
      <Grid
        container
        spacing={2}
        sx={{ mb: 4 }}
        aria-busy={loading || !data}
        role="region"
        aria-labelledby="tow-provider-home-roadside-title"
      >
        {loading || !data
          ? Array.from({ length: 5 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} lg={2} key={i}>
                <KpiCardSkeleton />
              </Grid>
            ))
          : (
            [
              { key: 'open',      value: data.roadside?.open      ?? 0, linkTo: '/roadside-assistance?status=OPEN' },
              { key: 'winner',    value: data.roadside?.winner    ?? 0, linkTo: '/roadside-assistance?status=WINNER' },
              { key: 'toArrive',  value: data.roadside?.toArrive  ?? 0, linkTo: '/roadside-assistance?status=TOARRIVE' },
              { key: 'inProcess', value: data.roadside?.inProcess ?? 0, linkTo: '/roadside-assistance?status=INPROCESS' },
              { key: 'delivered', value: data.roadside?.delivered ?? 0, linkTo: '/roadside-assistance?status=COMPLETED' },
            ] as const
          ).map(({ key, value, linkTo }) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={key}>
              <KpiCard
                label={t(`${tp}.roadside.${key}`)}
                value={value}
                icon={<KpiIcon kpiKey={key} />}
                tone={kpiVisual(key).tone}
                linkTo={linkTo}
              />
            </Grid>
          ))
        }
      </Grid>

      {/* The "Last Week" row lived here and was dropped: its four tiles were
          the Month row's four tiles over a shorter window, and two rows of the
          same four labels made the reader work out which was which every time.
          The 30-day chart below now carries the week-to-week shape, which is
          what the weekly row was really being read for. `data.week` is still
          returned by the API and simply not shown. */}

      {/* ------------------------------------------------------------------ */}
      {/* Section 2 — Month (4 KPI cards)                                    */}
      {/* towRequest and offerRequest drill down to /tow-requests with a     */}
      {/* 30-day startDate window (Angular month section, home.component.ts  */}
      {/* lines ~720 and ~728). winnerRequest and ammount have no drill-down. */}
      {/* ------------------------------------------------------------------ */}
      <Typography
        id="tow-provider-home-month-title"
        component="h2"
        sx={sectionEyebrowSx}
      >
        {t(`${tp}.month.title`)}
      </Typography>
      <Grid
        container
        spacing={2}
        aria-busy={loading || !data}
        role="region"
        aria-labelledby="tow-provider-home-month-title"
      >
        {loading || !data
          ? Array.from({ length: 4 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <KpiCardSkeleton />
              </Grid>
            ))
          : (
            [
              { key: 'towRequest',    value: data.month.towRequest,    linkTo: windowLink(30) },
              { key: 'offerRequest',  value: data.month.offerRequest,  linkTo: windowLink(30) },
              { key: 'winnerRequest', value: data.month.winnerRequest, linkTo: windowLink(30) },
              { key: 'ammount',       value: data.month.ammount,       linkTo: windowLink(30) },
            ] satisfies Array<{ key: string; value: number | string; linkTo: string | undefined }>
          ).map(({ key, value, linkTo }) => (
            <Grid item xs={12} sm={6} md={3} key={key}>
              <KpiCard
                label={t(`${tp}.month.${key}`)}
                value={value}
                icon={<KpiIcon kpiKey={key} />}
                tone={kpiVisual(key).tone}
                linkTo={linkTo}
                helpText={t(`${tp}.month.${key}Help`)}
              />
            </Grid>
          ))
        }
      </Grid>

      {/* ------------------------------------------------------------------ */}
      {/* Below the numbers — the work behind them.                          */}
      {/*                                                                    */}
      {/* The rows above are all counts, and a count is where a question     */}
      {/* starts rather than where it ends. These two answer the obvious     */}
      {/* follow-ups: *which* jobs are on my board right now, and am I       */}
      {/* winning the work I bid on.                                         */}
      {/*                                                                    */}
      {/* Queue first, chart second. One is work waiting to be done, the     */}
      {/* other is a look back — and on a narrow screen the stack order      */}
      {/* decides which one a dispatcher sees without scrolling.             */}
      {/* ------------------------------------------------------------------ */}
      <Grid container spacing={2} sx={{ mt: 0 }}>
        {/* The table renders first so it is the panel on top when these stack
            on a narrow screen — work to do beats a look back. The chart gets
            the wider column on desktop because 30 days of paired bars need
            the room. */}
        <Grid item xs={12} lg={5}>
          <ProviderActiveJobs />
        </Grid>
        <Grid item xs={12} lg={7}>
          <OfferConversionChart
            titleId="tow-provider-home-conversion-title"
            title={t(`${tp}.conversion.title`)}
            submittedLabel={t(`${tp}.conversion.submitted`)}
            wonLabel={t(`${tp}.conversion.won`)}
            helpText={t(`${tp}.conversion.help`)}
            helpAriaLabel={t(`${tp}.conversion.ariaLabel`)}
            data={conversionSeries}
            loading={loading}
          />
        </Grid>
      </Grid>

      <BackToTopFab />
    </Box>
  );
}
